import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { api, setAdminToken } from "../lib/api";
import { toast } from "sonner";

const loginBg =
  "https://static.prod-images.emergentagent.com/jobs/74c18d57-ed75-40f8-a004-70a02b215c45/images/b94ae4cc51874d52981786b96aeaa6bcd7a3d7552b7fbe059a5ce1ba260ca560.png";

function fmtRetry(retrySec) {
  if (!retrySec || retrySec < 0) return "shortly";
  const m = Math.ceil(retrySec / 60);
  return `${m} minute${m === 1 ? "" : "s"}`;
}

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState({ captcha_required: false });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [lockoutMsg, setLockoutMsg] = useState(null);
  const navigate = useNavigate();

  // Fetch the current challenge state on mount so a returning visitor sees captcha
  // if their IP had recent failures.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/login-challenge");
        if (!cancelled) {
          setChallenge(data || {});
          if (data?.locked_until) {
            const ms = new Date(data.locked_until).getTime() - Date.now();
            setLockoutMsg(`Locked. Try again in ${fmtRetry(Math.floor(ms / 1000))}.`);
          }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshChallenge = async () => {
    try {
      const { data } = await api.get("/admin/login-challenge");
      setChallenge(data || {});
    } catch {}
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    if (challenge.captcha_required && !captchaAnswer && !lockoutMsg) {
      toast.error("Please answer the captcha.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        password,
        captcha_token: challenge.captcha_token || undefined,
        captcha_answer: captchaAnswer || undefined,
      };
      const { data } = await api.post("/admin/login", body);
      setAdminToken(data.token);
      toast.success("Signed in");
      navigate("/admin/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const retry = parseInt(err.response.headers?.["retry-after"] || "900", 10);
        setLockoutMsg(`Too many failed attempts. Try again in ${fmtRetry(retry)}.`);
        toast.error("Locked out");
      } else {
        toast.error(err?.response?.data?.detail || "Invalid credentials");
      }
      // Always refresh challenge — server tells us if captcha is now needed
      setCaptchaAnswer("");
      await refreshChallenge();
    } finally {
      setSubmitting(false);
    }
  };

  const isLocked = !!lockoutMsg;

  return (
    <div className="relative min-h-screen bg-[#0A1628] text-[#F8FAFC] flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/60 via-[#0A1628]/80 to-[#0A1628]" />
      <div className="absolute -top-32 -right-20 w-[480px] h-[480px] so-glow-blue" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] so-glow-cyan" />

      <div className="relative w-full max-w-md px-6 so-rise">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/5 text-[11px] uppercase tracking-widest text-[#06B6D4] font-mono">
            <ShieldCheck size={12} /> Admin console
          </div>
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight">
            GenQuotes Admin
          </h1>
          <p className="mt-2 text-[#94A3B8] text-sm">
            Restricted to super administrators only.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl backdrop-blur-xl bg-[#0A1628]/60 border border-white/10 p-8 shadow-2xl"
        >
          <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
            Master password
          </label>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              autoFocus
              disabled={isLocked}
              className="so-input w-full rounded-lg pl-9 pr-4 py-3 text-sm disabled:opacity-60"
            />
          </div>

          {challenge.captcha_required && !isLocked && challenge.captcha_question && (
            <div className="mt-5" data-testid="admin-captcha-block">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={12} className="text-[#06B6D4]" />
                <span className="text-xs uppercase tracking-widest text-[#06B6D4] font-mono">
                  Verify you're human
                </span>
              </div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                {challenge.captcha_question}
              </label>
              <input
                data-testid="admin-captcha-input"
                type="text"
                inputMode="numeric"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="Your answer"
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
              />
              {challenge.failed_attempts > 0 && (
                <div className="mt-2 text-[11px] text-[#94A3B8] font-mono">
                  {challenge.failed_attempts} failed attempt{challenge.failed_attempts === 1 ? "" : "s"} from this address
                </div>
              )}
            </div>
          )}

          {isLocked && (
            <div
              data-testid="admin-lockout"
              className="mt-5 p-4 rounded-lg border border-[#EC4899]/30 bg-[#EC4899]/5 flex items-start gap-3"
            >
              <AlertTriangle size={16} className="text-[#EC4899] mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium text-[#EC4899]">Account locked</div>
                <div className="mt-1 text-xs text-[#94A3B8] leading-relaxed">{lockoutMsg}</div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || isLocked}
            data-testid="admin-login-submit"
            className="btn-blue w-full mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Signing in…</>
            ) : isLocked ? (
              "Locked"
            ) : (
              "Sign in to console"
            )}
          </button>

          <div className="mt-5 text-[11px] text-[#94A3B8] text-center font-mono uppercase tracking-widest">
            Single-tenant · password protected · 12h session
          </div>
        </form>
      </div>
    </div>
  );
}
